import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AiCostsResponse, ProcessingClient } from '../../web-api-client';
import { authenticatedFetch, getApiBaseUrl } from '../../api/apiConfig';

const AiCostsPanel: React.FC = () => {
  const { t } = useTranslation();
  const client = useMemo(() => new ProcessingClient(getApiBaseUrl(), authenticatedFetch), []);
  const [costs, setCosts] = useState<AiCostsResponse | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let active = true;
    let timer: number;
    const load = async () => {
      try {
        const result = await client.getApiOpsCost();
        if (active) { setCosts(result); setError(false); }
      } catch {
        if (active) setError(true);
      } finally {
        if (active) timer = window.setTimeout(load, 10000);
      }
    };
    void load();
    return () => { active = false; window.clearTimeout(timer); };
  }, [client]);
  const money = (value?: number | null) => value == null ? t('aiCosts.noCap')
    : new Intl.NumberFormat(undefined, { style: 'currency', currency: 'GBP' }).format(value);
  const capped = costs?.monthlyCapGbp != null && (costs.monthlyCostGbp ?? 0) >= costs.monthlyCapGbp;
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('aiCosts.title')}</Typography>
      {error && <Alert severity="error">{t('aiCosts.error')}</Alert>}
      {(capped || (costs?.heldJobCount ?? 0) > 0) && (
        <Alert severity="warning" sx={{ mb: 1 }}>{t('aiCosts.held', { count: costs?.heldJobCount ?? 0 })}</Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
        <Typography>{t('aiCosts.month', { value: money(costs?.monthlyCostGbp ?? 0) })}</Typography>
        <Typography>{t('aiCosts.monthCap', { value: money(costs?.monthlyCapGbp) })}</Typography>
        <Typography>{t('aiCosts.batchCap', { value: money(costs?.perBatchCapGbp) })}</Typography>
      </Stack>
      {(costs?.batches ?? []).map(batch => (
        <Box key={batch.batchId} sx={{ mt: 1 }}>
          <Typography variant="textSM">{t('aiCosts.batch', {
            date: batch.uploadedAt ? new Date(batch.uploadedAt).toLocaleString() : '',
            value: money(batch.costGbp), tokens: (batch.promptTokens ?? 0) + (batch.completionTokens ?? 0),
          })}</Typography>
        </Box>
      ))}
      <Typography color="text.secondary" variant="textSM" sx={{ display: 'block', mt: 1 }}>{t('aiCosts.estimate')}</Typography>
    </Paper>
  );
};

export default AiCostsPanel;
